import { motion, MotionProps } from "framer-motion";
import React, { useState } from "react";
import ArrowDownIcon from "@jq-icons/ArrowDown";
import ArrowUpIcon from "@jq-icons/ArrowUp";
import CheckIcon from "@jq-icons/Check";
import FlexItems from "../../../02-atoms/06-layout/03-flex";
import Text from "../../../02-atoms/13-text/03-text";
import styles from "./linksBlock.module.scss";
// Define a new interface that extends MotionProps
type MotionLiProps = MotionProps & React.HTMLAttributes<HTMLLIElement>;
const LiMotion = motion<MotionLiProps>("li");
type MotionULProps = MotionProps & React.HTMLAttributes<HTMLUListElement>;
const UlMotion = motion<MotionULProps>("ul");

const list = (maxHeight: string) => {
  return {
    visible: {
      maxHeight,
    },
    hidden: {
      maxHeight,
      transition: {
        ease: "easeInOut",
        when: "afterChildren",
      },
    },
  };
};

const listItemVars = {
  visible: { duration: 0.2, x: 0, display: "block" },
  off: { duration: 0.2, x: -40, display: "none" },
};

export interface LinkItemProps {
  /**
   * Child elements of the component.
   */
  children: React.ReactNode;

  /**
   * Whether to set the hidden class on the list item.
   */
  hidden: boolean;
}

export function LinkItem({ hidden, children }: LinkItemProps) {
  return (
    <LiMotion
      className={styles.listItem}
      initial={hidden ? "off" : "visible"}
      animate={hidden ? "off" : "visible"}
      variants={listItemVars}
    >
      <CheckIcon color="gray5" title="Check" /> {children}
    </LiMotion>
  );
}

export interface LinksBlockProps {
  /**
   * Title of the block
   */
  title: string;
  /**
   * Max items shown when the block is collapsed.
   */
  maxItems?: number;

  /**
   * An array of containing a objects with links and labels.
   */
  links: React.ReactNode[];

  /**
   * Label for showing more items action.
   */
  expandText: any;

  /**
   * Label for showing less items action.
   */
  collapseText: any;
}

export default function LinksBlock({
  title,
  maxItems = 5,
  links,
  expandText,
  collapseText,
}: LinksBlockProps) {
  const [expanded, setExpanded] = useState(false);

  let items;
  let action;
  if (expanded) {
    items = links.map((link, key) => (
      <LinkItem key={key} hidden={false}>
        {link}
      </LinkItem>
    ));

    action = (
      <button
        className={styles.expandButton}
        onClick={() => setExpanded(false)}
      >
        <Text tag="span" color="gray5">
          <FlexItems>
            {collapseText}
            <ArrowUpIcon title={collapseText} aria-hidden="true" />
          </FlexItems>
        </Text>
      </button>
    );
  } else {
    items = links.map((link, key) => (
      <LinkItem key={key} hidden={key >= maxItems}>
        {link}
      </LinkItem>
    ));

    action = links.length > maxItems && (
      <button className={styles.expandButton} onClick={() => setExpanded(true)}>
        <Text tag="span" color="gray5">
          <FlexItems>
            {expandText} <ArrowDownIcon title={expandText} aria-hidden="true" />
          </FlexItems>
        </Text>
      </button>
    );
  }

  return (
    <div className={styles.linkBlockWrapper}>
      <h2 className={styles.blockTitle}>{title}</h2>
      <UlMotion
        className={styles.listBlock}
        variants={list(`${maxItems * 2}rem`)}
      >
        {items}
      </UlMotion>
      {action}
    </div>
  );
}

export { LinksBlock };
